/**
 * Money & quantity arithmetic. Never use JS floats for money: every value is a
 * Decimal (decimal.js). DB columns are numeric(18,4) for money and numeric(18,3)
 * for quantities; PostgREST delivers numerics as JSON numbers, so always wrap
 * them with `D()` before doing arithmetic and send strings (toDb) back.
 */
import Decimal from 'decimal.js';

export const MoneyDecimal = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export type Dec = InstanceType<typeof MoneyDecimal>;
export type DecimalInput = Dec | string | number | null | undefined;

export const MONEY_SCALE = 4;
export const QTY_SCALE = 3;
export const RATE_SCALE = 8;

/** Coerce anything (number from PostgREST, string from a form, Decimal) into a Decimal. */
export function D(value: DecimalInput): Dec {
  if (value === null || value === undefined || value === '') return new MoneyDecimal(0);
  if (value instanceof MoneyDecimal) return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new RangeError(`Geçersiz sayı: ${value}`);
    // go through the shortest round-trip string, not the binary expansion
    return new MoneyDecimal(String(value));
  }
  return new MoneyDecimal(value);
}

export function isZero(value: DecimalInput): boolean {
  return D(value).isZero();
}

export function sum(values: Iterable<DecimalInput>): Dec {
  let acc = new MoneyDecimal(0);
  for (const v of values) acc = acc.plus(D(v));
  return acc;
}

/** Round to kuruş/cent (2 decimals, half-up) — invoice line arithmetic. */
export function round2(value: DecimalInput): Dec {
  return D(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

export function roundTo(value: DecimalInput, scale: number): Dec {
  return D(value).toDecimalPlaces(scale, Decimal.ROUND_HALF_UP);
}

/** Convert an amount with a rate (e.g. USD → TRY) rounded to the money scale. */
export function convert(amount: DecimalInput, rate: DecimalInput, scale = MONEY_SCALE): Dec {
  return roundTo(D(amount).times(D(rate)), scale);
}

/** Serialise for the database (string, fixed scale, no exponent). */
export function toDb(value: DecimalInput, scale = MONEY_SCALE): string {
  return roundTo(value, scale).toFixed(scale);
}

/** Percentage of a value: pct(1000, 20) = 200. */
export function percentOf(value: DecimalInput, pct: DecimalInput): Dec {
  return D(value).times(D(pct)).dividedBy(100);
}

/**
 * Parse user input written the Turkish way ("1.234,56", "12,5", "−488") or the
 * machine way ("1234.56"). Returns null when the text is not a number.
 * Rules: with a comma present, dots are thousands separators. Without a comma a
 * single dot followed by exactly three digits (e.g. "1.234") is a thousands
 * separator; otherwise it is the decimal point ("12.5", "0.500").
 */
export function parseNumber(input: string | null | undefined): Dec | null {
  if (input === null || input === undefined) return null;
  let s = String(input)
    .trim()
    .replace(/[\s\u00A0\u202F]/g, '')
    .replace(/[₺$€£]|TRY|USD|EUR|GBP|TL/gi, '')
    .replace(/[−–]/g, '-');
  if (s.startsWith('%')) s = s.slice(1);
  if (s.endsWith('%')) s = s.slice(0, -1);
  if (s === '' || s === '-' || s === '+') return null;
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    const dots = s.match(/\./g)?.length ?? 0;
    if (dots > 1 || /^[-+]?[1-9]\d{0,2}\.\d{3}$/.test(s)) s = s.replace(/\./g, '');
  }
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  try {
    return new MoneyDecimal(s);
  } catch {
    return null;
  }
}

export function compare(a: DecimalInput, b: DecimalInput): -1 | 0 | 1 {
  return D(a).comparedTo(D(b)) as -1 | 0 | 1;
}

export function max(...values: DecimalInput[]): Dec {
  return values.map(D).reduce((m, v) => (v.greaterThan(m) ? v : m));
}

export function min(...values: DecimalInput[]): Dec {
  return values.map(D).reduce((m, v) => (v.lessThan(m) ? v : m));
}
